import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { CreditCard, Plus, Gift, Wallet, History } from 'lucide-react';

const CreditsPage = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/credits'), api.get('/credits/history')])
      .then(([creditsRes, historyRes]) => {
        setWallet(creditsRes.data);
        setHistory(historyRes.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handlePurchase = async (credits) => {
    setPurchasing(credits);
    try {
      const res = await api.post('/credits/purchase', { credits });
      toast({
        title: 'Credits added!',
        description: `${res.data.creditsAdded} credits added to your wallet.`,
      });
      load();
    } catch (error) {
      toast({
        title: 'Purchase failed',
        description: error?.error?.message,
        variant: 'destructive',
      });
    } finally {
      setPurchasing(null);
    }
  };

  if (loading || !wallet) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Credits & Billing</h1>
        <p className="text-gray-600 mt-1">Manage your credits and purchase more</p>
      </div>

      {/* Current Balance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-8 bg-gradient-to-br from-pink-500 to-rose-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Free Credits</p>
              <p className="text-5xl font-bold mt-2">{wallet.freeCredits}</p>
              <p className="text-sm opacity-90 mt-2">Refill on your account anniversary</p>
            </div>
            <Gift className="w-16 h-16 opacity-50" />
          </div>
        </Card>
        <Card className="p-8 bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Purchased Credits</p>
              <p className="text-5xl font-bold mt-2">{wallet.purchasedCredits}</p>
              <p className="text-sm opacity-90 mt-2">Never expire</p>
            </div>
            <Wallet className="w-16 h-16 opacity-50" />
          </div>
        </Card>
      </div>

      <Card className="p-4 flex items-center gap-3 bg-orange-50 border-orange-200">
        <CreditCard className="w-5 h-5 text-orange-600 flex-shrink-0" />
        <p className="text-sm text-orange-800">
          <span className="font-semibold">{wallet.totalCredits} total credits</span> available — 1 credit = 1 guest invitation.
        </p>
      </Card>

      {/* Purchase Credits */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Purchase Credits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {wallet.packages.map((plan) => (
            <Card key={plan.credits} className="p-6 relative">
              {plan.bonusCredits > 0 && (
                <div className="absolute -top-3 right-3">
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded font-semibold">
                    +{plan.bonusCredits} FREE
                  </span>
                </div>
              )}
              <div className="text-center space-y-4">
                <div>
                  <p className="text-4xl font-bold">{plan.totalCredits}</p>
                  <p className="text-sm text-gray-600">Credits</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">₹{plan.price}</p>
                  <p className="text-sm text-gray-600">
                    ₹{(plan.price / plan.totalCredits).toFixed(2)} per credit
                  </p>
                </div>
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={purchasing === plan.credits}
                  onClick={() => handlePurchase(plan.credits)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {purchasing === plan.credits ? 'Processing...' : 'Buy Now'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Purchases here are simulated for demo purposes — no real payment is charged.
        </p>
      </div>

      {/* Transaction History */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <History className="w-5 h-5" />
          Recent Activity
        </h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">No transactions yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="text-sm font-medium">{tx.description}</p>
                  <p className="text-xs text-gray-500">{new Date(tx.createdAt).toLocaleString()}</p>
                </div>
                <span className={`text-sm font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-gray-600'}`}>
                  {tx.amount >= 0 ? '+' : ''}
                  {tx.amount}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Usage Info */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">How Credits Work</h2>
        <div className="space-y-3 text-sm text-gray-600">
          <p>• 1 credit = 1 guest invitation with QR code</p>
          <p>• Free credits and purchased credits are tracked separately; free credits are used first</p>
          <p>• Purchased credits never expire and can be used for any event</p>
          <p>• New users get 20 free credits on signup</p>
        </div>
      </Card>
    </div>
  );
};

export default CreditsPage;
